import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { ThemeProvider } from './contexts/ThemeContext';
import Loader from './components/common/Loader';

// Critical above-the-fold page (eager)
import Home from './pages/Home';

// Lazy-loaded pages (reduce initial bundle)
const Books = lazy(() => import('./pages/Books'));
const BookDetails = lazy(() => import('./pages/BookDetails'));
const Groups = lazy(() => import('./pages/Groups'));
const GroupDetails = lazy(() => import('./pages/GroupDetails'));
const Events = lazy(() => import('./pages/Events'));
const EventDetails = lazy(() => import('./pages/EventDetails'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Community = lazy(() => import('./pages/Community'));

// Non-critical widget (defer mount to reduce main-thread work)
const BookChat = lazy(() => import('./components/chat/BookChat'));

// Layouts
import PublicLayout from './components/layout/PublicLayout';
import RouteObserver from './components/common/RouteObserver';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  const [mountChat, setMountChat] = useState(false);

  useEffect(() => {
    // Defer the chat bundle until the browser is idle.
    let handle;
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      handle = window.requestIdleCallback(() => setMountChat(true), { timeout: 2500 });
      return () => window.cancelIdleCallback(handle);
    }

    handle = window.setTimeout(() => setMountChat(true), 1500);
    return () => window.clearTimeout(handle);
  }, []);

  const pageFallback = <Loader size="lg" className="min-h-[40vh]" />;

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <RouteObserver />
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route
                path="/books"
                element={
                  <Suspense fallback={pageFallback}>
                    <Books />
                  </Suspense>
                }
              />
              <Route
                path="/books/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <BookDetails />
                  </Suspense>
                }
              />
              <Route
                path="/groups"
                element={
                  <Suspense fallback={pageFallback}>
                    <Groups />
                  </Suspense>
                }
              />
              <Route
                path="/groups/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <GroupDetails />
                  </Suspense>
                }
              />
              <Route
                path="/events"
                element={
                  <Suspense fallback={pageFallback}>
                    <Events />
                  </Suspense>
                }
              />
              <Route
                path="/events/:id"
                element={
                  <Suspense fallback={pageFallback}>
                    <EventDetails />
                  </Suspense>
                }
              />
              <Route
                path="/about"
                element={
                  <Suspense fallback={pageFallback}>
                    <About />
                  </Suspense>
                }
              />
              <Route
                path="/contact"
                element={
                  <Suspense fallback={pageFallback}>
                    <Contact />
                  </Suspense>
                }
              />
              <Route
                path="/community"
                element={
                  <Suspense fallback={pageFallback}>
                    <Community />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>

        {mountChat ? (
          <Suspense fallback={null}>
            <BookChat />
          </Suspense>
        ) : null}

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              iconTheme: {
                primary: '#4CAF50',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#f44336',
                secondary: '#fff',
              },
            },
          }}
        />
      </ThemeProvider>
    </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
